import {
  faCopy,
  faPen,
  faTriangleExclamation,
  faZap,
} from '@fortawesome/pro-light-svg-icons';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { bulkCloneRiskRules } from '@/api/risk';
import type { RuleTemplateRule } from '@/api/risk/types';
import { HelpSheet } from '@/components/shared/HelpSheet';
import { PageContainer } from '@/components/shared/PageContainer';
import {
  PageHeaderCard,
  PageHeaderIdentity,
  PageHeaderMeta,
  PageHeaderTopRow,
} from '@/components/shared/PageHeaderCard';
import { RuleCard } from '@/components/shared/RiskRuleBuilder/RuleCard';
import { TagsField } from '@/components/shared/RiskRuleBuilder/TagsField';
import { TemplateHeaderCard } from '@/components/shared/RiskRuleBuilder/TemplateHeaderCard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/hooks/usePermissions';
import { useRuleTemplate } from '@/hooks/useRuleTemplate';
import { useStickyControls } from '@/hooks/useStickyControls';
import { useToggleActiveRuleTemplate } from '@/hooks/useToggleActiveRuleTemplate';
import { useTranslator } from '@/hooks/useTranslator';
import { cn } from '@/lib/utils/cn';
import { useSessionStore } from '@/store/sessionStore';
import { useAllRisks } from '../../../../-hooks/useAllRisks';

export const Route = createFileRoute(
  '/(client)/risk/(pages)/(detail)/rules/$id/',
)({
  component: RuleDetailPage,
});

function RuleDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { template, metrics } = useRuleTemplate(id);
  const { getTranslation: t } = useTranslator({ page: 'riskRules' });

  const { data: allRisks = [] } = useAllRisks();
  const riskOptions = allRisks.map(r => ({ key: r.id, label: r.title }));
  const [rules, setRules] = useState<RuleTemplateRule[]>([]);
  const [helpOpen, setHelpOpen] = useState(false);
  const d = template.data;
  // Condensed control strip (Activate/Clone/Edit) once the header scrolls
  // past — same pattern as CAPA and Certificates.
  const stickyControls = useStickyControls('--risk-rule-header-h');

  // Mirror the rules-table rule: editable only when I own it AND it isn't
  // published. Retraced presets / others' templates / published → no edit.
  const myCompanyId = useSessionStore(s => s.user?.companyId ?? '');
  const perms = usePermissions().risk;
  const isOwnedDraft =
    d?.createdByCompanyId === myCompanyId && d?.status !== 'PUBLISHED';
  const canEdit = perms.canEdit && isOwnedDraft;
  const activateMutation = useToggleActiveRuleTemplate(
    id,
    d
      ? {
          title: d.title,
          ...(d.description != null ? { description: d.description } : {}),
          tags: d.tags ?? [],
          ...(d.defaultSeverity != null
            ? { defaultSeverity: d.defaultSeverity }
            : {}),
          ...(d.scoreFormula != null ? { scoreFormula: d.scoreFormula } : {}),
        }
      : undefined,
    t('detail.activateSuccess'),
  );

  useEffect(() => {
    if (!d) return;
    setRules(d.rules?.length ? d.rules : []);
  }, [d]);

  const handleClone = async () => {
    try {
      await bulkCloneRiskRules([id]);
      await queryClient.invalidateQueries({ queryKey: ['risk', 'rules'] });
      toast.success(t('detail.cloneSuccess'));
      void navigate({ to: '/risk/rules' });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  if (template.isLoading) {
    return (
      <PageContainer className='gap-4'>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className='h-32 w-full' />
        ))}
      </PageContainer>
    );
  }

  const statusLabel =
    d?.status === 'PUBLISHED'
      ? t('builder.status.published')
      : t('builder.status.draft');

  // Rendered both in the full header's own action slot and, once that slot
  // has scrolled away, in the condensed strip below that takes over. Both
  // copies are always mounted (so the strip can fade in/out instead of
  // popping), which would otherwise put two same-named buttons (e.g.
  // "Clone") in the accessibility tree at once — real assistive tech and
  // `getByRole` queries alike would see both as live controls. `hidden`
  // marks whichever copy isn't the active one `aria-hidden`/`inert` so
  // exactly one is ever exposed, reachable, or focusable at a time.
  function renderActions(hidden: boolean) {
    return (
      d && (
        <div
          aria-hidden={hidden}
          inert={hidden}
          className='flex items-center gap-1.5'
        >
          {!d.isActive &&
            perms.canEdit &&
            d.createdByCompanyId === myCompanyId && (
              <AlertDialog>
                <AlertDialogTrigger
                  render={
                    <Button
                      type='button'
                      variant='outline'
                      className='gap-1.5'
                      disabled={activateMutation.isPending}
                    />
                  }
                >
                  <FontAwesomeIcon icon={faZap} className='size-3.5' />
                  {t('detail.activate')}
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('detail.activateDialog.title')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('detail.activateDialog.description')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className='flex justify-end gap-2 pt-2'>
                    <AlertDialogCancel>
                      {t('detail.activateDialog.cancel')}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      variant='default'
                      onClick={() => activateMutation.mutate(1)}
                    >
                      {t('detail.activateDialog.confirm')}
                    </AlertDialogAction>
                  </div>
                </AlertDialogContent>
              </AlertDialog>
            )}
          {perms.canCreate && (
            <Button
              type='button'
              variant='ghost'
              onClick={handleClone}
              className='gap-1.5'
            >
              <FontAwesomeIcon icon={faCopy} className='size-3.5' />
              {t('list.header.clone')}
            </Button>
          )}
          {canEdit && (
            <Button
              type='button'
              variant='outline'
              className='gap-1.5'
              onClick={() =>
                void navigate({
                  to: '/risk/rules/$id/edit',
                  params: { id },
                })
              }
            >
              <FontAwesomeIcon icon={faPen} className='size-3.5' />
              {t('list.editButton')}
            </Button>
          )}
          <Button
            type='button'
            variant='ghost'
            className='size-9 p-0'
            onClick={() => setHelpOpen(true)}
          >
            <FontAwesomeIcon icon={faCircleQuestion} className='text-sm' />
          </Button>
        </div>
      )
    );
  }

  return (
    <div className='@container flex flex-col h-full overflow-hidden'>
      {/* Padding moved off PageContainer (`p-0`) onto the content wrapper
          below: position: sticky's `top: 0` resolves against the scrolling
          ancestor's own padding edge, so padding left on PageContainer itself
          (the scroll root) would permanently inset the docked strip from the
          visible top of the scrollport — a gap you'd see page content
          through. Padding on a non-scrolling descendant has no such effect;
          it simply scrolls away like any other content. */}
      <PageContainer
        ref={stickyControls.scrollRef}
        className='overflow-y-auto p-0 min-[1600px]:p-0'
      >
        {d && (
          // No padding and no `gap` on this wrapper — the sentinel + condensed
          // strip must be direct, un-padded, un-nested children of it, both so
          // their containing block spans the whole scrollable page (not just
          // the header) and so the docked strip renders edge-to-edge rather
          // than inset. Padding instead lives on the header and the centered
          // column individually, like every other page in this rollout.
          <div className='flex flex-col'>
            {/* Full-width page header — the template's identity + CTAs, like the
                review / answer page header above the centered content. */}
            <div className='px-4 pt-3 min-[1600px]:px-6 min-[1600px]:pt-4'>
              <PageHeaderCard>
                <PageHeaderTopRow>
                  <PageHeaderIdentity
                    avatar={
                      <div className='w-11 h-11 rounded-xl grid place-items-center bg-primary/10 text-primary'>
                        <FontAwesomeIcon
                          icon={faTriangleExclamation}
                          className='size-5'
                        />
                      </div>
                    }
                    title={d.title || '—'}
                    titleAccessory={
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-semibold shrink-0',
                          d.status === 'PUBLISHED'
                            ? 'bg-success/15 text-success'
                            : 'bg-warning/15 text-warning',
                        )}
                      >
                        <span
                          className={cn(
                            'size-1.5 rounded-full',
                            d.status === 'PUBLISHED'
                              ? 'bg-success'
                              : 'bg-warning',
                          )}
                        />
                        {statusLabel}
                      </span>
                    }
                    subtitle={d.description || t('detail.subtitle')}
                    badges={
                      d.tags?.length ? (
                        <TagsField tags={d.tags} isReadOnly />
                      ) : undefined
                    }
                  />
                </PageHeaderTopRow>
                <PageHeaderMeta
                  items={[]}
                  action={renderActions(stickyControls.docked)}
                />
              </PageHeaderCard>
            </div>

            {/* Condensed control strip — takes over once the full header
                scrolls out of view, in both scroll directions. Always
                mounted inside a zero-height `sticky top-0` wrapper so the
                opacity/translate transition can run instead of popping in
                abruptly. */}
            <div
              ref={stickyControls.sentinelRef}
              aria-hidden
              className='h-px'
            />
            <div className='sticky top-0 z-20 h-0'>
              <div
                ref={stickyControls.barRef}
                className={cn(
                  'flex flex-wrap items-center justify-end gap-2 border-b border-border bg-background px-4 py-2 shadow-sm min-[1600px]:px-6 transition-[opacity,transform] duration-200 motion-reduce:transition-none',
                  stickyControls.docked
                    ? 'translate-y-0 opacity-100'
                    : '-translate-y-1 opacity-0 pointer-events-none',
                )}
              >
                {renderActions(!stickyControls.docked)}
              </div>
            </div>

            {/* Centered column (max-w-5xl): the template settings card followed
                by the rule cards, like the review section content. Owns its
                own padding — see the no-padding note on the wrapper above. */}
            <div className='mx-auto w-full max-w-5xl flex flex-col gap-5 px-4 pt-6 pb-3 min-[1600px]:px-6 min-[1600px]:pb-4'>
              <TemplateHeaderCard
                key={d.id}
                title={d.title}
                description={d.description ?? ''}
                tags={d.tags ?? []}
                defaultSeverity={d.defaultSeverity ?? 3}
                scoreFormula={d.scoreFormula ?? ''}
                status={d.status}
                // "Visibility" (Visible/Hidden) mirrors v1: it controls isActive —
                // i.e. whether the rule appears in the assessment template dropdown.
                visibility={d.isActive ? 'PUBLIC' : 'PRIVATE'}
                metrics={metrics}
                isReadOnly
                hideIdentity
                // Editable only for an owned, published rule — matching v1, where the
                // visibility switch appears only after publish. Drafts use Activate.
                {...(perms.canEdit &&
                d.createdByCompanyId === myCompanyId &&
                d.status === 'PUBLISHED'
                  ? {
                      onVisibilityChange: (v: 'PRIVATE' | 'PUBLIC') =>
                        activateMutation.mutate(v === 'PUBLIC' ? 1 : 0),
                    }
                  : {})}
              />

              <div className='flex items-center gap-2.5'>
                <h2 className='text-sm font-semibold tracking-tight'>
                  {t('detail.rules')}
                </h2>
                <span className='inline-flex items-center h-5 px-2 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tabular-nums'>
                  {rules.length}
                </span>
              </div>

              {rules.map((rule, i) => (
                <RuleCard
                  key={i}
                  rule={rule}
                  index={i}
                  risks={riskOptions}
                  isReadOnly
                />
              ))}
            </div>
          </div>
        )}
      </PageContainer>

      <HelpSheet
        articleId='8960666'
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </div>
  );
}
